import { useQuery } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import {
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Pagination,
  Select,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components';
import { useApi } from '@/shared/api';
import { AuditLogRow } from './AuditLogRow';
import { auditEntityLabel } from './systemLabels';

const ENTITY_TYPES = [
  'All',
  'Property',
  'Booking',
  'GuestProfile',
  'CleaningTask',
  'DamageReport',
  'PropertyFile',
  'OnboardingLink',
  'OutboxMessage',
] as const;

/**
 * Das Änderungsprotokoll des Workspace — append-only, nach Bereich und Vorgang filterbar.
 *
 * <p>Der Vorgangsname bleibt bewusst der Rohwert aus dem Backend: es gibt mehrere Dutzend davon,
 * sie sind freie Zeichenketten, und die Suche filtert genau nach ihrem Wortlaut. Übersetzt sind
 * die Bereiche, die Spaltenköpfe und die Zustände.</p>
 */
export function AuditLogPage() {
  const api = useApi();
  const [entityType, setEntityType] = useState<(typeof ENTITY_TYPES)[number]>('All');
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['system', 'audit', entityType, actionFilter, page],
    queryFn: () =>
      api.system.auditLogs({
        entityType: entityType === 'All' ? undefined : entityType,
        action: actionFilter || undefined,
        page,
        pageSize,
      }),
    placeholderData: (prev) => prev,
  });

  return (
    <>
      <PageHeader
        eyebrow="System"
        title="Änderungs"
        titleAccent="protokoll"
        description={
          data
            ? `${data.total} festgehaltene Änderungen in diesem Workspace. Einträge werden nur ergänzt, nie verändert.`
            : 'Änderungen werden geladen…'
        }
      />

      <div className="mb-4 grid gap-3 sm:max-w-xl sm:grid-cols-2">
        <FormField label="Bereich">
          <Select
            value={entityType}
            onChange={(e) => {
              setEntityType(e.target.value as typeof entityType);
              setPage(1);
            }}
          >
            {ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {auditEntityLabel(t)}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Vorgang enthält" hint="z. B. Created, Deleted, PropertyMoved">
          <Input
            type="search"
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value);
              setPage(1);
            }}
          />
        </FormField>
      </div>

      {error ? (
        <EmptyState icon="Error" title="Konnte das Änderungsprotokoll nicht laden" />
      ) : !isLoading && data && data.items.length === 0 ? (
        <EmptyState
          icon="Database"
          title="Keine Einträge"
          description="Für diesen Bereich und diesen Vorgang ist nichts festgehalten. Versuchen Sie einen anderen Filter."
        />
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Zeitpunkt</TableHead>
                <TableHead>Vorgang</TableHead>
                <TableHead>Betroffen</TableHead>
                <TableHead>Ausgelöst von</TableHead>
                <TableHead>
                  <span className="sr-only">Einzelheiten</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.items ?? []).map((entry) => (
                // Vorher stand hier ein Fragment ohne Key um zwei Zeilen — React kann daran
                // keinen Schlüssel tragen und hat die ganze Liste bei jeder Änderung neu gebaut.
                <Fragment key={entry.id}>
                  <AuditLogRow
                    entry={entry}
                    expanded={expandedRow === entry.id}
                    onToggle={() => setExpandedRow(expandedRow === entry.id ? null : entry.id)}
                  />
                </Fragment>
              ))}
            </TableBody>
          </Table>
          {data && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={Number(data.total)}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </>
  );
}
